import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterFeatureSource,
  CharacterSheetInterface,
  Sense,
  SENSES,
} from "@dndModels";

import ModalOrDrawer from "@components/display/ModalOrDrawer";
import StringBox from "@components/input/strings/StringBox";
import FloatMenu from "@components/input/menus/FloatMenu";
import CharacterFeatureFromSource from "@dndComponents/character/features/CharacterFeatureFromSource";

interface Props {
  character: Character;
  char_interface: CharacterSheetInterface;
  disabled: boolean;
}

const customization_map: { [type: string]: string } = {
  "Extra Modifiers": "Modifier",
  "Override Senses": "Override"
};

export default function CharacterSensesButton({ 
  character,
  char_interface,
  disabled,
}: Props) {
  const [open, setOpen] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [view, setView] = useState("Features");

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderSenseFeatures = () => {
    const sense_features = character.sense_features;
    return (
      <>
        <Grid
          className="header"
          size={{
            xs: 12
          }}>
          Sense Features
        </Grid>
        { sense_features.sort((a,b) => { return a.name.localeCompare(b.name) }).map((char_feature, key) => {
          const source = character.all_char_feature_sources.find(o => 
            o.true_id === char_feature.source_true_id);
          if (source) {
            return (
              <Grid
                key={key}
                size={{
                  xs: 12
                }}>
                <CharacterFeatureFromSource
                  character={character}
                  obj={char_feature}
                  source={source}
                />
              </Grid>
            );
          }
          return null;
        })}
      </>
    );
  }

  const renderSenseCustomizations = () => {
    return (
      <>
        <Grid
          style={{ fontWeight: "bold" }}
          size={{
            xs: 12
          }}>
          { view }
        </Grid>
        { SENSES.map(sense => {
          return (
            <Grid
              key={sense._id}
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              { renderSenseCustomization(sense, customization_map[view]) }
            </Grid>
          );
        })}
      </>
    );
  }

  const renderSenseCustomization = (sense: Sense, customization_type: string) => {
    const parent = char_interface.sense_customizations[sense._id];
    const customization = parent ? parent[customization_type] : null;
    const char_sense = char_interface.senses.find(o => o.sense_id === sense._id);
    return (
      <>
        <Grid
          size={{
            xs: 6
          }}>
          { sense.name }: { char_sense ? `${char_sense.range} ft.` : "0 ft." }
        </Grid>
        <Grid
          size={{
            xs: customization && customization.value !== null ? 4 : 6
          }}>
          <StringBox
            name="Value"
            value={ customization && customization.value !== null ? `${customization.value}` : "" }
            onBlur={(changed: string) => {
              if (!char_interface.sense_customizations[sense._id]) {
                char_interface.sense_customizations[sense._id] = { [customization_type]: { value: null, notes: "" }};
              } else if (!char_interface.sense_customizations[sense._id][customization_type]) {
                char_interface.sense_customizations[sense._id][customization_type] = { value: null, notes: "" };
              }
              if (changed === "") {
                delete char_interface.sense_customizations[sense._id][customization_type];
              } else {
                char_interface.sense_customizations[sense._id][customization_type].value = +changed;
              }
              character.subscription_holder.recalculate();
              setReloading(true);
            }}
          />
        </Grid>
        { customization && customization.value !== null &&
          <Grid
            size={{
              xs: 2
            }}>
            <StringBox
              name={ `Source/Notes` }
              value={ customization ? customization.notes : "" }
              onBlur={(changed: string) => {
                if (!char_interface.sense_customizations[sense._id]) {
                  char_interface.sense_customizations[sense._id] = { [customization_type]: { value: null, notes: "" }};
                } else if (!char_interface.sense_customizations[sense._id][customization_type]) {
                  char_interface.sense_customizations[sense._id][customization_type] = { value: null, notes: "" };
                }
                char_interface.sense_customizations[sense._id][customization_type].notes = changed;
                character.subscription_holder.recalculate();
              }}
            />
          </Grid>
        }
      </>
    );
  }

  const renderView = () => {
    switch(view) {
      // case "Current": return renderCurrent();
      case "Features": return renderSenseFeatures();
      // case "Bonus Proficiencies": return renderBonusSenseProfs();
      default: return renderSenseCustomizations();
    }
  }

  const renderSensesInput = () => {
    const options = ["Features","Extra Modifiers","Override Senses"];
    return (
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 12
        }}>
        <Grid
          className="centered header"
          size={{
            xs: 12
          }}>
          Manage Senses
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <FloatMenu
            options={options}
            selected={view}
            onSelect={setView}
          />
        </Grid>
        { renderView() }
      </Grid>
    );
  }

  return (
    <Grid container spacing={0} direction="row">
      <Grid
        className={`centered header ${ disabled ? "" : "clickable"}`}
        onClick={() => {
          if (!disabled) {
            setOpen(true);
          }
        }}
        size={{
          xs: 12
        }}>
        Senses
      </Grid>
      <ModalOrDrawer
        open={ open } 
        onClose={() => {
          setOpen(false);
          character.subscription_holder.modalClosed();
        }}>
        { open && renderSensesInput() }
      </ModalOrDrawer>
    </Grid>
  );
}
